import { NgClass, NgFor, NgIf, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  ContentChild,
  EventEmitter,
  Input,
  OnInit,
  Optional,
  Output,
  Self,
  TemplateRef,
  ViewEncapsulation,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  NgControl,
} from '@angular/forms';
import { MatSelectModule } from '@angular/material/select';
import { ConfigurationService } from '@sc/data-access-configuration';
import { ErrorMessagesService } from '../../services';
import { ErrorMessage } from '@sc/data-access-models';
import { UtilsValidators } from '../../validators';
import { ErrorComponent } from '../error';
import { randomId } from '@sc/utils';

@Component({
  selector: 'sc-select',
  encapsulation: ViewEncapsulation.None,
  styleUrls: ['./select.component.scss'],
  imports: [
    NgIf,
    NgFor,
    NgClass,
    NgTemplateOutlet,
    ErrorComponent,
    MatSelectModule,
  ],
  template: `
    <mat-form-field [appearance]="appearance" [class.no-label]="!label">
      <mat-label
        [class.asterisk]="hasRequiredValidator(ngControl?.control)"
        *ngIf="label"
        >{{ label }}</mat-label
      >
      <mat-select
        [value]="value"
        [placeholder]="placeholder"
        [panelClass]="panelClass"
        [disabled]="disabled"
        (valueChange)="onValueChange($event)"
        [compareWith]="compareWith()"
        [aria-describedby]="isError() ? errorId : undefined"
        (focusin)="onFocusin()"
        (focusout)="onFocusout()"
        class="sc-design-text-3"
      >
        <mat-select-trigger>
          <ng-container
            *ngTemplateOutlet="triggerTemplate; context: { $implicit: value }"
          ></ng-container>
          <div *ngIf="!triggerTemplate">{{ value }}</div>
        </mat-select-trigger>

        <mat-option
          *ngFor="let option of options"
          [value]="optionKey ? option[optionKey] : option"
          [disabled]="isDisabled(option)"
        >
          <ng-container
            *ngTemplateOutlet="optionTemplate; context: { $implicit: option }"
          ></ng-container>
          <div *ngIf="!optionTemplate">
            {{ optionKey ? option[optionKey] : option }}
          </div>
        </mat-option>
      </mat-select>
    </mat-form-field>
    <sc-error
      *ngIf="!!displayError()"
      [ngClass]="{
        outline: appearance === 'outline',
        'sc-conf-error-on-input-focus': focus
      }"
      [id]="errorId"
      [error]="getError()"
      [displayCondition]="displayError()"
    ></sc-error>
  `,
})
export class SelectComponent implements ControlValueAccessor, OnInit {
  @ContentChild('triggerTemplate') triggerTemplate: TemplateRef<any>;
  @ContentChild('optionTemplate') optionTemplate: TemplateRef<any>;

  @Input() appearance: any = 'outline';
  @Input() value: any;
  @Input() label: string;
  @Input() options: any[];
  @Input() optionKey: any;
  @Input() optionValue: any;
  @Input() disabledOptionKey: any;
  @Input() disabledOptionFn: any;
  @Input() compareWithFn;
  @Input() panelClass = '';
  @Input() error: ErrorMessage;
  @Input() placeholder = '';
  @Input() forceDisplayError = false;
  @Input() disabled = false;
  @Output() valueChange = new EventEmitter<any>();

  focus: boolean;
  errorId: string;

  onChange: any = () => {};
  onTouched: any = () => {};
  onKeyPress: any = () => true;

  constructor(
    @Self()
    @Optional()
    public ngControl: NgControl,
    public changeDetectorRef: ChangeDetectorRef,
    private readonly errorMessageService: ErrorMessagesService,
    private readonly configurationService: ConfigurationService
  ) {
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
    //random id for the mat-error id and aria-describedby of the input
    this.errorId = randomId(8);

    this.appearance =
      this.configurationService.getConfig()?.themeOptions?.matInputAppearance ||
      'outline';
  }

  ngOnInit(): void {
    this.panelClass = this.panelClass + ' select-custom-menu-panel';
  }

  onValueChange(value: any) {
    this.writeValue(value);
    this.onChange(value);
    this.valueChange.emit(value);
  }

  isDisabled(option) {
    return this.disabledOptionKey
      ? option[this.disabledOptionKey]
      : this.disabledOptionFn
      ? this.disabledOptionFn(option)
      : false;
  }

  writeValue(value: string): void {
    this.value = value;
    this.changeDetectorRef.detectChanges();
  }

  registerOnKeyPress(fn: any): void {
    this.onKeyPress = fn;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onFocusin() {
    this.focus = true;
  }

  onFocusout() {
    this.focus = false;
  }

  compareWith() {
    return this.compareWithFn ?? this.defaultCompareWith;
  }

  defaultCompareWith(o1: any, o2: any) {
    return o1 === o2;
  }

  hasRequiredValidator(control: AbstractControl): boolean {
    return UtilsValidators.hasRequiredValidator(control);
  }

  displayError() {
    return !!this.getError() && this.isError();
  }

  isError() {
    return (
      (this.ngControl?.invalid &&
        (this.ngControl?.dirty || this.ngControl?.touched)) ||
      this.forceDisplayError
    );
  }

  getError() {
    if (!this.ngControl?.control && !this.error) {
      return null;
    }

    return (
      this.error ??
      this.errorMessageService.getError(this.ngControl?.control, this.label)
    );
  }
}
